import { useState, useEffect } from 'react';
import { Bell, BellOff, Smartphone, CheckCircle, XCircle, RefreshCw, Lock } from '../../icons/wp-icons';
import {
  isPushSupported,
  isStandalonePWA,
  getPermissionStatus,
  requestPushPermission,
  subscribeToPush,
  unsubscribeFromPush
} from '../../utils/pushNotifications';

export default function PushNotificationSettings({ readOnly = false }) {
  const [isSupported, setIsSupported] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isSecureContext, setIsSecureContext] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setChecking(true);
    setError(null);

    try {
      const supported = isPushSupported();
      const standalone = isStandalonePWA();
      const status = getPermissionStatus();
      const secure = window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost';

      setIsSupported(supported);
      setIsStandalone(standalone);
      setIsSecureContext(secure);
      setPermissionStatus(status);

      // Check if actually subscribed (with timeout)
      if (supported && status === 'granted') {
        try {
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Timeout')), 3000)
          );
          const registration = await Promise.race([
            navigator.serviceWorker.ready,
            timeoutPromise
          ]);
          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);
        } catch (e) {
          console.error('Error checking subscription:', e);
          setIsSubscribed(false);
        }
      } else {
        setIsSubscribed(false);
      }

      setLastChecked(new Date().toLocaleTimeString('de-DE'));
    } catch (e) {
      console.error('Status check error:', e);
      setError('Fehler beim Prüfen: ' + e.message);
    } finally {
      setChecking(false);
    }
  };

  const handleEnable = async () => {
    setLoading(true);
    setError(null);

    try {
      const permission = await requestPushPermission();
      setPermissionStatus(permission);

      if (permission === 'granted') {
        await subscribeToPush();
        setIsSubscribed(true);
      } else if (permission === 'denied') {
        setError('Push-Benachrichtigungen wurden blockiert. Bitte in den Geräteeinstellungen aktivieren.');
      }
    } catch (err) {
      console.error('Push enable error:', err);
      setError(err.message || 'Fehler beim Aktivieren');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    setError(null);

    try {
      await unsubscribeFromPush();
      setIsSubscribed(false);
    } catch (err) {
      console.error('Push disable error:', err);
      setError(err.message || 'Fehler beim Deaktivieren');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPrompt = () => {
    localStorage.removeItem('push-permission-dismissed');
    localStorage.removeItem('pwa-hint-dismissed');
    alert('Hinweis zurückgesetzt. Bitte Seite neu laden.');
  };

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // Initial loading state
  if (checking && !lastChecked) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card overflow-hidden">
      {/* Header */}
      <div className="p-4 sm:p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-light rounded-lg shrink-0">
            <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-semibold">Push-Benachrichtigungen</h2>
            <p className="text-xs sm:text-sm text-text-muted hidden sm:block">
              Erhalte Benachrichtigungen auf diesem Gerät
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6">

      {/* Status Info */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-sm text-gray-600">HTTPS/Sicher</span>
          <span className={`flex items-center gap-1 text-sm ${isSecureContext ? 'text-green-600' : 'text-red-600'}`}>
            {isSecureContext ? <Lock className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {isSecureContext ? 'Ja' : 'Nein - erforderlich'}
          </span>
        </div>

        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-sm text-gray-600">Push unterstützt</span>
          <span className={`flex items-center gap-1 text-sm ${isSupported ? 'text-green-600' : 'text-red-600'}`}>
            {isSupported ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {isSupported ? 'Ja' : 'Nein'}
          </span>
        </div>

        {isIOS && (
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Als App installiert</span>
            <span className={`flex items-center gap-1 text-sm ${isStandalone ? 'text-green-600' : 'text-orange-600'}`}>
              {isStandalone ? <CheckCircle className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
              {isStandalone ? 'Ja' : 'Nein - erforderlich'}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-sm text-gray-600">Berechtigung</span>
          <span className={`text-sm ${
            permissionStatus === 'granted' ? 'text-green-600' :
            permissionStatus === 'denied' ? 'text-red-600' :
            'text-gray-600'
          }`}>
            {permissionStatus === 'granted' ? 'Erlaubt' :
             permissionStatus === 'denied' ? 'Blockiert' :
             permissionStatus === 'unsupported' ? 'Nicht verfügbar' :
             'Nicht angefragt'}
          </span>
        </div>

        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-gray-600">Status</span>
          <span className={`flex items-center gap-1 text-sm ${isSubscribed ? 'text-green-600' : 'text-gray-600'}`}>
            {isSubscribed ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
            {isSubscribed ? 'Aktiv' : 'Inaktiv'}
          </span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* HTTPS Required Hint */}
      {!isSecureContext && (
        <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <p className="text-sm text-orange-800 font-medium mb-2">
            HTTPS erforderlich für Push-Benachrichtigungen
          </p>
          <p className="text-sm text-orange-700">
            Push-Benachrichtigungen funktionieren nur über HTTPS.
            Bitte nutze die App über <strong>https://crm.werkpilot.de</strong> oder localhost.
          </p>
        </div>
      )}

      {/* iOS PWA Hint */}
      {isIOS && !isStandalone && isSecureContext && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800 font-medium mb-2">
            Push-Benachrichtigungen erfordern die App-Installation:
          </p>
          <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
            <li>Tippe auf das Teilen-Symbol (Quadrat mit Pfeil)</li>
            <li>Wähle "Zum Home-Bildschirm"</li>
            <li>Öffne die App vom Home-Bildschirm</li>
          </ol>
        </div>
      )}

      {/* Action Buttons */}
      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          {isSupported && permissionStatus !== 'denied' && !isSubscribed && (
            <button
              onClick={handleEnable}
              disabled={loading || (isIOS && !isStandalone)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Bell className="w-4 h-4" />
              )}
              Aktivieren
            </button>
          )}

          {isSubscribed && (
            <button
              onClick={handleDisable}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-button hover:bg-gray-200 transition disabled:opacity-50"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <BellOff className="w-4 h-4" />
              )}
              Deaktivieren
            </button>
          )}

          <button
            onClick={checkStatus}
            disabled={checking}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-button hover:bg-gray-200 transition disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
            {checking ? 'Prüfe...' : 'Status prüfen'}
          </button>

          {lastChecked && (
            <span className="text-xs text-gray-400 self-center">
              Zuletzt: {lastChecked}
            </span>
          )}

          <button
            onClick={handleResetPrompt}
            className="flex items-center gap-2 px-4 py-2 text-gray-500 hover:text-gray-700 transition text-sm"
          >
            Hinweis zurücksetzen
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
